import { whenThirdPartyAllowed } from '@/lib/defer-third-party'
import type { GptSize, GptSizeMapEntry } from '@/lib/ad-slots'

type GptSlot = {
  getSlotElementId: () => string
  getAdUnitPath?: () => string
  defineSizeMapping?: (mapping: unknown) => unknown
  addService: (s: unknown) => unknown
}

type SlotRenderEvent = {
  slot: GptSlot & { isEmpty?: () => boolean }
  isEmpty?: boolean
  size?: [number, number] | null
  /** rewardedSlotReady eventinde reklamı görünür yapar */
  makeRewardedVisible?: () => void
  /** rewardedSlotGranted eventinde ödül bilgisi */
  payload?: { type: string; amount: number } | null
}

type SizeMappingBuilder = {
  addSize: (viewport: [number, number], sizes: GptSize | GptSize[]) => SizeMappingBuilder
  build: () => unknown
}

declare global {
  interface Window {
    googletag?: {
      cmd: Array<() => void>
      apiReady?: boolean
      defineSlot?: (
        path: string,
        size: GptSize | GptSize[],
        id: string,
      ) => GptSlot | null
      defineOutOfPageSlot?: (
        path: string,
        divOrFormat: string | number,
      ) => GptSlot | null
      enums?: {
        OutOfPageFormat?: {
          REWARDED?: number
          INTERSTITIAL?: number
          TOP_ANCHOR?: number
          BOTTOM_ANCHOR?: number
        }
      }
      sizeMapping?: () => SizeMappingBuilder
      destroySlots?: (slots?: unknown[]) => void
      setConfig?: (config: {
        singleRequest?: boolean
        safeFrame?: { forceSafeFrame?: boolean }
        adsenseAttributes?: { page_url?: string | null; document_language?: string | null } | null
      }) => void
      pubads?: () => {
        enableSingleRequest?: () => void
        collapseEmptyDivs: (collapse?: boolean, collapseBeforeFetch?: boolean) => void
        setForceSafeFrame?: (force: boolean) => void
        set?: (key: string, value: string) => void
        refresh?: (slots?: unknown[]) => void
        addEventListener?: (event: string, cb: (event: SlotRenderEvent) => void) => void
        removeEventListener?: (event: string, cb: (event: SlotRenderEvent) => void) => void
        getSlots?: () => GptSlot[]
      }
      enableServices?: () => void
      display?: (idOrSlot: string | GptSlot) => void
    }
  }
}

let scriptPromise: Promise<void> | null = null
let pubadsConfigured = false
let gptServicesEnabled = false
let renderListenerAttached = false

const definedDivIds = new Set<string>()
const slotByDivId = new Map<string, unknown>()
const renderWaiters = new Map<string, Set<(empty: boolean) => void>>()

function notifyRender(divId: string, empty: boolean) {
  const waiters = renderWaiters.get(divId)
  if (!waiters?.size) return
  renderWaiters.delete(divId)
  for (const cb of waiters) cb(empty)
}

function attachRenderListener() {
  if (renderListenerAttached) return
  const gt = window.googletag
  const pubads = gt?.pubads?.()
  if (!pubads?.addEventListener) return
  pubads.addEventListener('slotRenderEnded', (event) => {
    const divId = event.slot.getSlotElementId()
    const empty =
      event.isEmpty ??
      event.slot.isEmpty?.() ??
      !(event.size && event.size[0] > 0 && event.size[1] > 0)
    notifyRender(divId, Boolean(empty))
  })
  renderListenerAttached = true
}

/**
 * GPT/AdSense yüklemeden önce: lab UA’da hiç açılmaz;
 * gerçek kullanıcıda ilk etkileşim veya load+fallback.
 */
export async function waitForAdIdle(minDelayMs = 0): Promise<void> {
  if (typeof window === 'undefined') return
  await whenThirdPartyAllowed({
    fallbackMs: Math.max(3_500, minDelayMs || 0),
  })
}

function isGptReady(): boolean {
  return Boolean(window.googletag?.apiReady || window.googletag?.defineSlot)
}

export function ensureGptScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()
  if (scriptPromise) return scriptPromise

  scriptPromise = (async () => {
    await waitForAdIdle(400)
    window.googletag = window.googletag || { cmd: [] }
    const existing = document.getElementById('gpt-js') as HTMLScriptElement | null
    if (existing) {
      if (existing.dataset.loaded === '1' || isGptReady()) return
      await new Promise<void>((resolve) => {
        const done = () => resolve()
        existing.addEventListener('load', done, { once: true })
        existing.addEventListener('error', done, { once: true })
        // Script zaten yüklenmiş olabilir (load event kaçmış)
        if (existing.dataset.loaded === '1' || isGptReady()) done()
        else window.setTimeout(done, 2500)
      })
      return
    }
    await new Promise<void>((resolve) => {
      const s = document.createElement('script')
      s.id = 'gpt-js'
      s.async = true
      s.src = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js'
      // CSP strict-dynamic: GPT + creative zincirinin nonce ile güvenilmesi
      const nonce = document.querySelector('script[nonce]')?.getAttribute('nonce')
      if (nonce) s.setAttribute('nonce', nonce)
      s.onload = () => {
        s.dataset.loaded = '1'
        resolve()
      }
      s.onerror = () => resolve()
      document.head.appendChild(s)
    })
  })()

  return scriptPromise
}

function resolveAdsPageUrl(): string | null {
  if (typeof window === 'undefined') return null
  const host = window.location.hostname.toLowerCase()
  // beta/localhost: GAM inventory çoğu zaman prod domaine bağlı
  if (
    host === 'beta.haber.com' ||
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host.endsWith('.local')
  ) {
    const origin =
      (typeof process !== 'undefined' &&
        process.env.NEXT_PUBLIC_SITE_URL?.replace(/\/$/, '')) ||
      window.location.origin
    return `${origin}${window.location.pathname}${window.location.search}`
  }
  return null
}

function ensurePubadsServices() {
  const gt = window.googletag
  if (!gt?.pubads || pubadsConfigured) return
  const pageUrl = resolveAdsPageUrl()
  try {
    if (pageUrl) {
      gt.setConfig?.({
        adsenseAttributes: { page_url: pageUrl },
      })
      gt.pubads().set?.('page_url', pageUrl)
    }
  } catch {
    /* ignore */
  }
  // www.haber.com ile aynı: SRA açık. collapseEmptyDivs YOK —
  // boş fill'de display:none + 0×0 shell fill retry'ı öldürüyordu.
  try {
    // GPT: enableSingleRequest() deprecated → setConfig({ singleRequest })
    if (typeof gt.setConfig === 'function') {
      gt.setConfig({ singleRequest: true })
    } else {
      gt.pubads().enableSingleRequest?.()
    }
  } catch {
    /* ignore */
  }
  attachRenderListener()
  pubadsConfigured = true
}

export function waitForGamSlotRender(divId: string, timeoutMs = 12000): Promise<boolean> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') {
      resolve(false)
      return
    }

    let settled = false
    const finish = (empty: boolean) => {
      if (settled) return
      settled = true
      window.clearTimeout(timer)
      renderWaiters.delete(divId)
      resolve(!empty)
    }

    const timer = window.setTimeout(() => {
      // Timeout: slotRenderEnded gelmediyse boş say — boş yer kaplamasın
      finish(true)
    }, timeoutMs)

    const set = renderWaiters.get(divId) ?? new Set()
    set.add(finish)
    renderWaiters.set(divId, set)
  })
}

function applySizeMapping(slot: GptSlot, mapping: GptSizeMapEntry[] | undefined) {
  if (!mapping?.length) return
  const gt = window.googletag
  if (!gt?.sizeMapping || !slot.defineSizeMapping) return
  try {
    let builder = gt.sizeMapping()
    for (const row of mapping) {
      builder = builder.addSize([row.minWidth, 0], row.sizes)
    }
    slot.defineSizeMapping(builder.build())
  } catch {
    /* sizeMapping desteklenmiyorsa yoksay */
  }
}

export function displayGamSlot(
  divId: string,
  adUnitPath: string,
  sizes: GptSize[],
  sizeMapping?: GptSizeMapEntry[],
): void {
  if (typeof window === 'undefined') return
  if (!adUnitPath || !divId) return

  window.googletag = window.googletag || { cmd: [] }
  window.googletag.cmd.push(() => {
    try {
      const gt = window.googletag
      if (!gt?.defineSlot || !gt.pubads || !gt.enableServices || !gt.display) return

      ensurePubadsServices()

      if (!definedDivIds.has(divId)) {
        const defined = gt.defineSlot(adUnitPath, sizes, divId)
        if (!defined) return
        applySizeMapping(defined, sizeMapping)
        defined.addService(gt.pubads())
        definedDivIds.add(divId)
        slotByDivId.set(divId, defined)

        const isLateSlot = gptServicesEnabled
        if (!gptServicesEnabled) {
          gt.enableServices()
          gptServicesEnabled = true
        }

        gt.display(divId)

        if (isLateSlot && gt.pubads()?.refresh) {
          gt.pubads().refresh!([defined])
        }
        return
      }

      if (!gptServicesEnabled) {
        gt.enableServices()
        gptServicesEnabled = true
      }

      gt.display(divId)
      const existing = slotByDivId.get(divId)
      if (existing && gt.pubads()?.refresh) {
        gt.pubads().refresh!([existing])
      }
    } catch {
      /* GPT hatası — sessiz */
    }
  })
}

export async function bootstrapGamSlot(
  divId: string,
  adUnitPath: string,
  sizes: GptSize[],
  sizeMapping?: GptSizeMapEntry[],
): Promise<boolean> {
  await ensureGptScript()
  // Waiter ÖNCE — display slotRenderEnded’i kaçırmasın
  const rendered = waitForGamSlotRender(divId)
  displayGamSlot(divId, adUnitPath, sizes, sizeMapping)
  return rendered
}

export function refreshGamSlot(divId: string): Promise<boolean> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') {
      resolve(false)
      return
    }

    window.googletag = window.googletag || { cmd: [] }
    window.googletag.cmd.push(() => {
      try {
        const gt = window.googletag
        const slot = slotByDivId.get(divId)
        const refresh = gt?.pubads?.()?.refresh
        if (!slot || !refresh) {
          resolve(false)
          return
        }

        const rendered = waitForGamSlotRender(divId)
        refresh([slot])
        void rendered.then(resolve)
      } catch {
        resolve(false)
      }
    })
  })
}

export type RewardedAdResult = 'granted' | 'closed' | 'unfilled' | 'error'

/**
 * Resmî GPT rewarded (ödüllü) reklam akışı — Offerwall benzeri kapılar için.
 * https://developers.google.com/publisher-tag/samples/display-rewarded-ad
 *
 * - `rewardedSlotReady` → reklam hazır, görünür yapılır
 * - `rewardedSlotGranted` → kullanıcı ödülü kazandı → 'granted'
 * - `rewardedSlotClosed` (ödülsüz) → 'closed'
 * - Doluluk yok / zaman aşımı (ad blocker dahil) → 'unfilled'
 *
 * GPT scripti tek sefer yüklenir (ensureGptScript); SSR'da çağrılırsa 'error'.
 */
export async function showRewardedAd(
  adUnitPath: string,
  timeoutMs = 20_000,
): Promise<RewardedAdResult> {
  if (typeof window === 'undefined' || !adUnitPath) return 'error'
  await ensureGptScript()

  return new Promise<RewardedAdResult>((resolve) => {
    window.googletag = window.googletag || { cmd: [] }
    window.googletag.cmd.push(() => {
      try {
        const gt = window.googletag
        const rewardedFormat = gt?.enums?.OutOfPageFormat?.REWARDED
        if (
          !gt?.defineOutOfPageSlot ||
          rewardedFormat == null ||
          !gt.pubads ||
          !gt.display
        ) {
          resolve('error')
          return
        }

        ensurePubadsServices()

        const slot = gt.defineOutOfPageSlot(adUnitPath, rewardedFormat)
        if (!slot) {
          // Sayfada zaten aktif rewarded slot vb. — kapıyı kilitli bırakma
          resolve('error')
          return
        }
        slot.addService(gt.pubads())

        const pubads = gt.pubads()
        let resolved = false
        let granted = false

        const listeners: Array<[string, (e: SlotRenderEvent) => void]> = []
        const cleanup = () => {
          for (const [ev, fn] of listeners) {
            try {
              pubads.removeEventListener?.(ev, fn)
            } catch {
              /* ignore */
            }
          }
          try {
            gt.destroySlots?.([slot])
          } catch {
            /* ignore */
          }
        }
        const settle = (result: RewardedAdResult) => {
          if (resolved) return
          resolved = true
          window.clearTimeout(timer)
          resolve(result)
        }

        const onReady = (e: SlotRenderEvent) => {
          if (e.slot !== slot) return
          window.clearTimeout(timer)
          e.makeRewardedVisible?.()
        }
        const onGranted = (e: SlotRenderEvent) => {
          if (e.slot !== slot) return
          granted = true
          // Ödül verildi — kapıyı hemen aç; slot, kullanıcı kapatınca temizlenir
          settle('granted')
        }
        const onClosed = (e: SlotRenderEvent) => {
          if (e.slot !== slot) return
          settle(granted ? 'granted' : 'closed')
          cleanup()
        }
        const onRender = (e: SlotRenderEvent) => {
          if (e.slot !== slot) return
          const empty =
            e.isEmpty ??
            e.slot.isEmpty?.() ??
            false
          if (empty) {
            settle('unfilled')
            cleanup()
          }
        }

        listeners.push(
          ['rewardedSlotReady', onReady],
          ['rewardedSlotGranted', onGranted],
          ['rewardedSlotClosed', onClosed],
          ['slotRenderEnded', onRender],
        )
        for (const [ev, fn] of listeners) pubads.addEventListener?.(ev, fn)

        // Ad blocker / doluluk yok: makul sürede yanıt gelmezse vazgeç
        const timer = window.setTimeout(() => {
          settle('unfilled')
          cleanup()
        }, timeoutMs)

        if (!gptServicesEnabled) {
          gt.enableServices?.()
          gptServicesEnabled = true
        }
        gt.display(slot)
      } catch {
        resolve('error')
      }
    })
  })
}

/** haber.com — tüm GAM slotlarını yenile */
export function refreshAllGamAds(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') {
      resolve()
      return
    }
    window.googletag = window.googletag || { cmd: [] }
    window.googletag.cmd.push(() => {
      try {
        window.googletag?.pubads?.()?.refresh?.()
      } catch {
        /* ignore */
      }
      resolve()
    })
  })
}
